import React from 'react';

/**
 * RobbieMark (TASK-103) — the locked "4g" mascot, pure CSS (no image assets).
 * Canonical geometry (tokens, `design/theme.js` `robbie`): head 52×46 r15,
 * pill eyes 6×18 gap 9, detached pill hands 14×28 r7 with a ~3px gap, ±8° tilt.
 * Hands appear at EVERY size — scale, never drop. States live in the eyes:
 * white = idle · azure ring = listening · shimmer = thinking · azure glow +
 * raised right hand + ping beacon = insight · arcs + both hands up = done ·
 * right-hand wave = waving (login) · still mark + raised bulb shaking with an
 * azure glow = working (steady glow under reduced motion) · eyes reading left↔right + gentle float = searching. Animations are CSS-only and run under
 * `motion-safe:` so `prefers-reduced-motion` gets a still mascot.
 * Violet is Robbie-only — this component is the main licensed user.
 */
export type RobbieState = 'idle' | 'listening' | 'thinking' | 'insight' | 'done' | 'waving' | 'searching' | 'working';
export type RobbieSize = 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface RobbieMarkProps {
  /** Mascot state; defaults to idle. */
  state?: RobbieState;
  /** xxs badge/chip · xs inline rows/bars · sm chat avatar/panel header · md nav logo · lg spec tile · xl login hero. */
  size?: RobbieSize;
  /** Tip/insight context: azure glowing dot top-right (bulb signal) on ANY state. */
  beacon?: boolean;
  /** Violet halo for the mark on navy chrome (login/sidebar logo). */
  glow?: boolean;
  className?: string;
  'aria-label'?: string;
}

interface Geometry {
  frame: [number, number];
  head: { x: number; y: number; w: number; h: number; r: number };
  eye: { w: number; h: number; gap: number };
  hand: { w: number; h: number; r: number; top: number; leftX: number; rightX: number };
}

/** Per-size geometry, pixel-matched to the v2 mock pages (see task file). */
const GEOMETRY: Record<RobbieSize, Geometry> = {
  xxs: { frame: [19, 12], head: { x: 3, y: 0, w: 12, h: 12, r: 4 }, eye: { w: 2, h: 6, gap: 2 }, hand: { w: 3, h: 6, r: 2, top: 4, leftX: 0, rightX: 0 } },
  xs: { frame: [28, 18], head: { x: 5, y: 0, w: 18, h: 18, r: 6 }, eye: { w: 2.5, h: 7, gap: 2.5 }, hand: { w: 4.5, h: 9, r: 3, top: 6, leftX: 0, rightX: 0 } },
  sm: { frame: [38, 26], head: { x: 6, y: 0, w: 26, h: 26, r: 8 }, eye: { w: 3.5, h: 10, gap: 3.5 }, hand: { w: 5, h: 11, r: 4, top: 9, leftX: 0, rightX: 0 } },
  md: { frame: [42, 30], head: { x: 7, y: 0, w: 28, h: 28, r: 9 }, eye: { w: 4, h: 11, gap: 4 }, hand: { w: 5.5, h: 12, r: 4, top: 10, leftX: 0, rightX: 0 } },
  lg: { frame: [100, 64], head: { x: 24, y: 9, w: 52, h: 46, r: 15 }, eye: { w: 6, h: 18, gap: 9 }, hand: { w: 14, h: 28, r: 7, top: 18, leftX: 6, rightX: 6 } },
  xl: { frame: [120, 82], head: { x: 21, y: 4, w: 78, h: 68, r: 22 }, eye: { w: 9, h: 27, gap: 13 }, hand: { w: 17, h: 34, r: 9, top: 30, leftX: 0, rightX: 2 } },
};

const px = (n: number) => `${Math.round(n * 10) / 10}px`;

/** Shimmer eye fill while thinking (3f gradient — mascot-only, not a token). */
const shimmerFill: React.CSSProperties = {
  background: 'linear-gradient(180deg,#DCE6FD 25%,#B9C8F9 50%,#DCE6FD 75%)',
  backgroundSize: '100% 200%',
};

const Eye: React.FC<{ g: Geometry; state: RobbieState; second?: boolean }> = ({ g, state, second }) => {
  const { eye } = g;
  if (state === 'done') {
    // Happy arc: derived from the eye pill (lg reference 15×8, radius top-only).
    return (
      <span
        className="bg-white"
        style={{ width: px(eye.h * (15 / 18)), height: px(eye.h * (8 / 18)), borderRadius: '9999px 9999px 0 0' }}
      />
    );
  }
  const listening = state === 'listening'; // eyes widen slightly under the ring
  const base: React.CSSProperties = {
    width: px(listening ? eye.w * (8 / 6) : eye.w),
    height: px(listening ? eye.h * (21 / 18) : eye.h),
  };
  if (state === 'thinking') {
    return (
      <span
        className="rounded-round motion-safe:animate-shimmer"
        style={{ ...base, ...shimmerFill, animationDelay: second ? '0.2s' : undefined }}
      />
    );
  }
  if (state === 'insight') {
    return <span className="rounded-round bg-signal-eye shadow-azure-glow" style={base} />;
  }
  return <span className="rounded-round bg-white" style={base} />;
};

const Hand: React.FC<{ g: Geometry; side: 'left' | 'right'; state: RobbieState }> = ({ g, side, state }) => {
  const { head, hand } = g;
  const base: React.CSSProperties = { width: px(hand.w), height: px(hand.h), borderRadius: px(hand.r) };
  const rest: React.CSSProperties = {
    ...base,
    top: px(hand.top),
    [side === 'left' ? 'left' : 'right']: px(side === 'left' ? hand.leftX : hand.rightX),
    transform: `rotate(${side === 'left' ? -8 : 8}deg)`,
  };
  if (state === 'done') {
    // Both hands up: bottoms land ~24% into the head, tilted outward ±20°.
    // Horizontal inset derives from the head's side margin so the pill stays
    // DETACHED at every size (locked mascot rule): the former fixed 9/46
    // ratio reproduced the lg mock (inset 9) but collapsed the gap onto the
    // head at smaller sizes — hands read as horns. lg still lands at 9.
    const sideMargin = side === 'left' ? head.x : g.frame[0] - head.x - head.w;
    const gap = Math.max(1, (1 / 46) * head.h);
    const inset = Math.max(0, sideMargin - hand.w - gap);
    return (
      <span
        className="absolute bg-robbie"
        style={{
          ...base,
          top: px(head.y + 0.24 * head.h - hand.h),
          [side === 'left' ? 'left' : 'right']: px(inset),
          transform: `rotate(${side === 'left' ? -20 : 20}deg)`,
        }}
      />
    );
  }
  if (side === 'right' && (state === 'insight' || state === 'working')) {
    // Raised right hand: slightly longer pill, bottom ~29% into the head, 22°.
    // Working shakes it around its base; the rest of the mark stays still.
    const working = state === 'working';
    const h = hand.h * (30 / 28);
    return (
      <span
        data-bulb={working ? '' : undefined}
        className={`absolute bg-robbie ${working ? 'origin-bottom motion-safe:animate-robbie-bulb motion-reduce:shadow-bulb-glow' : ''}`}
        style={{
          ...base,
          height: px(h),
          top: px(head.y + 0.29 * head.h - h),
          right: px(Math.max(0, hand.rightX - (2 / 46) * head.h)),
          transform: 'rotate(22deg)',
        }}
      />
    );
  }
  if (side === 'right' && state === 'waving') {
    // Wave: longer pill raised beside the head, rocking 14°–34° from its base.
    const h = hand.h * (38 / 34);
    return (
      <span
        className="absolute origin-bottom bg-robbie motion-safe:animate-robbie-wave"
        style={{
          ...base,
          height: px(h),
          top: px(head.y + 0.29 * head.h - h),
          right: px(hand.rightX),
          transform: 'rotate(24deg)', // still pose under prefers-reduced-motion
        }}
      />
    );
  }
  return <span className="absolute bg-robbie" style={rest} />;
};

/** Azure bulb signal — ping halo + glowing dot (white core once big enough). */
const Beacon: React.FC<{ g: Geometry; raisedHand: boolean }> = ({ g, raisedHand }) => {
  const s = g.head.h / 46;
  const d = Math.max(7, 17 * s);
  const pos: React.CSSProperties = {
    width: px(d),
    height: px(d),
    top: px(-4 * s),
    right: px((raisedHand ? 14 : 2) * s), // clears the raised insight hand
  };
  return (
    <>
      <span
        className="absolute rounded-round motion-safe:animate-robbie-ping"
        style={{ ...pos, background: 'rgba(96,196,250,0.5)' }}
      />
      <span
        className="box-content flex absolute items-center justify-center rounded-round border-2 border-white bg-signal shadow-azure-glow"
        style={pos}
      >
        {d >= 12 && <span className="rounded-round bg-white" style={{ width: px(d / 3.5), height: px(d / 3.5) }} />}
      </span>
    </>
  );
};

export const RobbieMark: React.FC<RobbieMarkProps> = ({
  state = 'idle',
  size = 'md',
  beacon = false,
  glow = false,
  className = '',
  'aria-label': ariaLabel,
}) => {
  const g = GEOMETRY[size];
  const ring = Math.max(2, Math.round(4 * (g.head.h / 46)));
  const searching = state === 'searching';
  return (
    <span
      role="img"
      aria-label={ariaLabel ?? `Robbie — ${state}`}
      className={`relative inline-block shrink-0 align-middle ${searching ? 'motion-safe:animate-robbie-float' : ''} ${className}`}
      style={{
        width: px(g.frame[0]),
        height: px(g.frame[1]),
        ...(searching
          ? ({ '--robbie-scan': px(g.head.w * 0.09), '--robbie-float': px(g.head.h * 0.05) } as React.CSSProperties)
          : {}),
      }}
    >
      <span
        className={`absolute flex items-center justify-center bg-robbie ${glow ? 'shadow-nav-glow' : ''}`}
        style={{
          left: px(g.head.x),
          top: px(g.head.y),
          width: px(g.head.w),
          height: px(g.head.h),
          borderRadius: px(g.head.r),
          gap: px(g.eye.gap),
          boxShadow: state === 'listening' ? `0 0 0 ${ring}px rgba(96,196,250,0.35)` : undefined,
        }}
      >
        {searching ? (
          <span className="flex motion-safe:animate-robbie-scan" style={{ gap: px(g.eye.gap) }}>
            <Eye g={g} state={state} />
            <Eye g={g} state={state} second />
          </span>
        ) : (
          <>
            <Eye g={g} state={state} />
            <Eye g={g} state={state} second />
          </>
        )}
      </span>
      <Hand g={g} side="left" state={state} />
      <Hand g={g} side="right" state={state} />
      {(state === 'insight' || beacon) && <Beacon g={g} raisedHand={state === 'insight'} />}
    </span>
  );
};

export default RobbieMark;
